import type { FormEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { DialogPattern } from '@/components/ui/dialog-pattern'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { filterChipTriggerClasses } from '@/components/shared/filter-bar-styles'
import { useAccountType } from '@/hooks/useAccountType'
import { useModuleViews } from '@/hooks/useModuleViews'
import { useProjectTaskViews } from '@/hooks/useProjectTaskViews'
import { useViewUrlState } from '@/hooks/useViewUrlState'
import { cn } from '@/lib/utils'
import {
  ArrowCounterClockwiseIcon,
  CaretDownIcon,
  CheckIcon,
  FloppyDiskIcon,
  LinkIcon,
  StarIcon,
  TrashIcon,
  UserIcon,
  UsersIcon,
  WarningIcon,
} from '@phosphor-icons/react'
import { toast } from 'sonner'

import {
  ModuleTypeEnum,
  PreferenceScopeEnum,
  ViewConfig,
} from '@scrambl/shared'

interface ViewSelectorProps {
  moduleType: ModuleTypeEnum
  canManage?: boolean
  getCurrentConfig: () => ViewConfig | null
  applyConfig: (config: ViewConfig) => Promise<void> | void
  className?: string
  /** Optional project ID for project-scoped views (used for project tasks) */
  projectId?: string
  /**
   * ScQL currently in the surface's query bar, for pages that have one
   * (tickets today). Saving a view stores this on the SAME record as the
   * column/grouping config, so a view is "this query + this layout".
   */
  currentQuery?: string
  /** Applies a saved view's query back into the query bar */
  onApplyQuery?: (query: string) => void
}

const sanitizeViewConfig = (config: ViewConfig | null): ViewConfig | null => {
  if (!config) return null
  const filters = config.filters ? { ...config.filters } : undefined
  if (filters && 'page' in filters) {
    delete (filters as Record<string, unknown>).page
  }
  return { ...config, filters }
}

const sortValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(sortValue)
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>
    return Object.keys(record)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = sortValue(record[key])
        return acc
      }, {})
  }
  return value
}

const stableStringify = (value: unknown): string => {
  return JSON.stringify(sortValue(value))
}

const isEmptyValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return true
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') return Object.keys(value).length === 0
  return false
}

const normalizeConfig = (
  config: ViewConfig | null | undefined,
): ViewConfig | null => {
  if (!config) return null
  const normalized: ViewConfig = {}
  if (config.filters && !isEmptyValue(config.filters)) {
    normalized.filters = config.filters
  }
  if (config.columnFilters && !isEmptyValue(config.columnFilters)) {
    normalized.columnFilters = config.columnFilters
  }
  if (config.columnVisibility && !isEmptyValue(config.columnVisibility)) {
    normalized.columnVisibility = config.columnVisibility
  }
  if (config.columnOrder && !isEmptyValue(config.columnOrder)) {
    normalized.columnOrder = config.columnOrder
  }
  if (config.columnWidths && !isEmptyValue(config.columnWidths)) {
    normalized.columnWidths = config.columnWidths
  }
  if (
    config.groupBy !== undefined &&
    config.groupBy !== null &&
    config.groupBy !== ''
  ) {
    normalized.groupBy = config.groupBy
  }
  // Return null if the normalized config is empty (no meaningful differences)
  if (Object.keys(normalized).length === 0) {
    return null
  }
  return normalized
}

const VIEW_SCOPE_DETAILS: Record<
  PreferenceScopeEnum,
  {
    label: string
    description: string
    Icon: typeof UserIcon
    color: string
    bgColor: string
  }
> = {
  [PreferenceScopeEnum.USER]: {
    label: 'Personal view',
    description: 'Only you can see this view.',
    Icon: UserIcon,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
  },
  [PreferenceScopeEnum.ORGANIZATION]: {
    label: 'Shared view',
    description: 'Visible to everyone in your organization.',
    Icon: UsersIcon,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-500/10',
  },
}

export function ViewSelector({
  moduleType,
  canManage = false,
  getCurrentConfig,
  applyConfig,
  className,
  projectId,
  currentQuery,
  onApplyQuery,
}: ViewSelectorProps) {
  const { isPersonal } = useAccountType()
  // Use project-scoped views when projectId is provided, otherwise use module-level views
  const moduleViews = useModuleViews({ moduleType, canManage })
  const projectViews = useProjectTaskViews({
    projectId: projectId || '',
    canManage,
    enabled: !!projectId,
  })

  // Select the appropriate views based on whether projectId is provided
  const isProjectScoped = !!projectId
  const {
    views,
    userViews,
    activeView,
    activeViewId,
    isLoading,
    setActiveViewId,
    saveActiveView,
    createView,
    deleteView,
  } = isProjectScoped ? projectViews : moduleViews

  // Handle the difference in naming between module views (orgViews) and project views (sharedViews)
  const orgViews = isProjectScoped
    ? projectViews.sharedViews
    : moduleViews.orgViews
  const orgDefaultViewId = isProjectScoped
    ? projectViews.defaultViewId
    : moduleViews.orgDefaultViewId
  const setOrgDefaultViewId = isProjectScoped
    ? projectViews.setDefaultViewId
    : moduleViews.setOrgDefaultViewId

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [pendingScope, setPendingScope] = useState<PreferenceScopeEnum>(
    PreferenceScopeEnum.USER,
  )
  const [viewName, setViewName] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [isSwitchDialogOpen, setIsSwitchDialogOpen] = useState(false)
  const [pendingSwitchViewId, setPendingSwitchViewId] = useState<string | null>(
    null,
  )
  const [isSwitching, setIsSwitching] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  // Track the last applied view ID to suppress false "edited" detection during state sync
  const [lastAppliedViewId, setLastAppliedViewId] = useState<string | null>(
    null,
  )
  // Track if we're in the middle of applying a config (to distinguish from user changes)
  const isApplyingConfigRef = useRef(false)
  // Track the previous config string to detect actual user changes
  const prevConfigStringRef = useRef<string | null>(null)

  const currentConfigRaw = getCurrentConfig()
  const currentConfig = sanitizeViewConfig(currentConfigRaw)
  const hasConfig = !!currentConfig
  const scopeDetails = VIEW_SCOPE_DETAILS[pendingScope]
  const ScopeIcon = scopeDetails.Icon
  const isNameValid = viewName.trim().length > 0
  const isModified = (() => {
    // If we just applied this view, don't show as modified until user makes actual changes
    if (lastAppliedViewId && lastAppliedViewId === activeViewId) {
      return false
    }
    if (!activeView) {
      return false
    }
    // The query is part of the artifact on surfaces that carry one, so it is
    // checked before the config short-circuits on "both empty".
    const isQueryModified =
      !!onApplyQuery &&
      (currentQuery ?? '').trim() !== (activeView.query ?? '').trim()
    if (isQueryModified) return true

    const normalizedCurrent = normalizeConfig(currentConfig)
    const normalizedActive = normalizeConfig(activeView.config)
    // Both null means no config differences
    if (normalizedCurrent === null && normalizedActive === null) {
      return false
    }
    const currentStr = stableStringify(normalizedCurrent)
    const activeStr = stableStringify(normalizedActive)
    return currentStr !== activeStr
  })()

  // A shared, unmodified record view is addressable by id, so the link stays
  // short and keeps tracking the view if its owner edits it later. Personal
  // views and unsaved edits are shared as a self-contained inline payload —
  // a colleague can't open someone else's PRIVATE row.
  const shareableSavedViewId =
    activeView?.source === 'record' &&
    activeView.scope === PreferenceScopeEnum.ORGANIZATION &&
    !isModified
      ? activeView.id
      : null

  const { getShareUrl } = useViewUrlState({
    moduleType,
    getCurrentConfig,
    applyConfig,
    preferSavedViewId: shareableSavedViewId,
    viewName: activeView?.name,
  })

  const handleCopyShareLink = async () => {
    const url = getShareUrl()
    if (!url) {
      toast.error('There is nothing to share in this view yet')
      return
    }
    try {
      await navigator.clipboard.writeText(url)
      toast.success('Link copied — anyone on your team can open this view')
    } catch {
      toast.error('Failed to copy link')
    }
  }

  // Effect to detect user changes and clear the lastAppliedViewId suppression flag
  useEffect(() => {
    // Skip if we don't have a suppression flag set
    if (!lastAppliedViewId) {
      // Just update the previous config ref for future comparisons
      prevConfigStringRef.current = stableStringify(
        normalizeConfig(currentConfig),
      )
      return
    }

    // Skip if we're in the middle of applying a config
    if (isApplyingConfigRef.current) {
      return
    }

    const currentStr = stableStringify(normalizeConfig(currentConfig))

    // If the config changed from what we last saw, user made a change
    if (
      prevConfigStringRef.current !== null &&
      prevConfigStringRef.current !== currentStr
    ) {
      setLastAppliedViewId(null)
    }

    // Update the ref for future comparisons
    prevConfigStringRef.current = currentStr
  }, [currentConfig, lastAppliedViewId])

  const canDeleteActiveView = Boolean(
    activeView && (activeView.scope === PreferenceScopeEnum.USER || canManage),
  )

  const applyViewQuery = (view: { query?: string | null }) => {
    if (!onApplyQuery) return
    onApplyQuery(view.query ?? '')
  }

  const handleSaveCurrent = async () => {
    const config = sanitizeViewConfig(getCurrentConfig())
    if (!config) return
    await saveActiveView(config, onApplyQuery ? (currentQuery ?? '') : undefined)
  }

  const handleRevertChanges = async () => {
    if (!activeView) return
    // Mark that we're applying a config (not a user change)
    isApplyingConfigRef.current = true
    // Mark this view as "just applied" to suppress false modification detection
    setLastAppliedViewId(activeView.id)
    applyViewQuery(activeView)
    await applyConfig(activeView.config)
    // Update the prevConfigString to match what we just applied
    prevConfigStringRef.current = stableStringify(
      normalizeConfig(activeView.config),
    )
    // Clear the applying flag after a short delay to allow React to process the state update
    setTimeout(() => {
      isApplyingConfigRef.current = false
    }, 100)
    // Close the popover after revert completes
    setOpen(false)
  }

  const handleOpenCreateDialog = (scope: PreferenceScopeEnum) => {
    setPendingScope(scope)
    setViewName('')
    setIsCreateDialogOpen(true)
  }

  const handleSetOrgDefault = async () => {
    if (!activeView || activeView.scope !== PreferenceScopeEnum.ORGANIZATION)
      return
    await setOrgDefaultViewId(activeView.id)
  }

  const handleDialogOpenChange = (open: boolean) => {
    setIsCreateDialogOpen(open)
    if (!open) {
      setViewName('')
      setIsSaving(false)
    }
  }

  const handleCreateSubmit = async (event?: FormEvent) => {
    event?.preventDefault()
    const trimmedName = viewName.trim()
    if (!trimmedName) return
    const config = sanitizeViewConfig(getCurrentConfig())
    if (!config) return
    setIsSaving(true)
    try {
      await createView(
        trimmedName,
        config,
        pendingScope,
        onApplyQuery ? (currentQuery ?? '') : undefined,
      )
      handleDialogOpenChange(false)
    } finally {
      setIsSaving(false)
    }
  }

  const handleSwitchDialogOpenChange = (open: boolean) => {
    setIsSwitchDialogOpen(open)
    if (!open) {
      setPendingSwitchViewId(null)
      setIsSwitching(false)
    }
  }

  const handleDeleteDialogOpenChange = (open: boolean) => {
    setIsDeleteDialogOpen(open)
    if (!open) {
      setIsDeleting(false)
    }
  }

  const applyViewById = async (viewId: string) => {
    const view = views.find(item => item.id === viewId)
    if (!view) return
    // Mark that we're applying a config (not a user change)
    isApplyingConfigRef.current = true
    // Mark this view as "just applied" to suppress false modification detection
    setLastAppliedViewId(viewId)
    // Apply config first to update UI state, then update active view ID
    applyViewQuery(view)
    await applyConfig(view.config)
    // Update the prevConfigString to match what we just applied
    prevConfigStringRef.current = stableStringify(normalizeConfig(view.config))
    await setActiveViewId(viewId)
    // Clear the applying flag after a short delay to allow React to process the state update
    setTimeout(() => {
      isApplyingConfigRef.current = false
    }, 100)
  }

  const handleSelectView = async (viewId: string) => {
    if (viewId === activeViewId) {
      return
    }
    if (isModified) {
      setPendingSwitchViewId(viewId)
      setIsSwitchDialogOpen(true)
      return
    }
    await applyViewById(viewId)
  }

  const handleSaveAndSwitch = async () => {
    if (!pendingSwitchViewId) return
    const nextId = pendingSwitchViewId
    const targetView = views.find(item => item.id === nextId)
    if (!targetView) return

    setIsSwitching(true)
    // Mark that we're applying a config (not a user change)
    isApplyingConfigRef.current = true
    // Mark this view as "just applied" to suppress false modification detection
    setLastAppliedViewId(nextId)
    try {
      // Save current changes first
      if (currentConfig) {
        await saveActiveView(
          currentConfig,
          onApplyQuery ? (currentQuery ?? '') : undefined,
        )
      }
      // Apply new config first to update UI, then update active view ID
      applyViewQuery(targetView)
      await applyConfig(targetView.config)
      // Update the prevConfigString to match what we just applied
      prevConfigStringRef.current = stableStringify(
        normalizeConfig(targetView.config),
      )
      await setActiveViewId(nextId)
      // Clear the applying flag after a short delay
      setTimeout(() => {
        isApplyingConfigRef.current = false
      }, 100)
    } finally {
      handleSwitchDialogOpenChange(false)
    }
  }

  const handleDiscardAndSwitch = async () => {
    if (!pendingSwitchViewId) return
    const nextId = pendingSwitchViewId
    const targetView = views.find(item => item.id === nextId)
    if (!targetView) return

    setIsSwitching(true)
    // Mark that we're applying a config (not a user change)
    isApplyingConfigRef.current = true
    // Mark this view as "just applied" to suppress false modification detection
    setLastAppliedViewId(nextId)
    try {
      // Apply new config first to update UI, then update active view ID
      applyViewQuery(targetView)
      await applyConfig(targetView.config)
      // Update the prevConfigString to match what we just applied
      prevConfigStringRef.current = stableStringify(
        normalizeConfig(targetView.config),
      )
      await setActiveViewId(nextId)
      // Clear the applying flag after a short delay
      setTimeout(() => {
        isApplyingConfigRef.current = false
      }, 100)
    } finally {
      handleSwitchDialogOpenChange(false)
    }
  }

  const handleDeleteActiveView = async () => {
    if (!activeView) return
    setIsDeleting(true)
    try {
      await deleteView(activeView.id)
    } finally {
      handleDeleteDialogOpenChange(false)
    }
  }
  const dialogTitle =
    pendingScope === PreferenceScopeEnum.ORGANIZATION
      ? 'Save shared view'
      : 'Save personal view'
  const dialogDescription =
    pendingScope === PreferenceScopeEnum.ORGANIZATION
      ? isPersonal
        ? 'Visible to everyone in your space.'
        : 'Visible to everyone in your organization.'
      : 'Only you can see this view.'

  const [open, setOpen] = useState(false)

  // View item component
  const ViewItem = ({
    view,
    isActive,
    isOrgDefault,
  }: {
    view: { id: string; name: string; scope: PreferenceScopeEnum }
    isActive: boolean
    isOrgDefault: boolean
  }) => {
    const isShared = view.scope === PreferenceScopeEnum.ORGANIZATION
    const Icon = isShared ? UsersIcon : UserIcon
    const color = isShared ? 'text-emerald-500' : 'text-blue-500'
    const bgColor = isShared ? 'bg-emerald-500/10' : 'bg-blue-500/10'
    const showEdited = isActive && isModified

    return (
      <button
        onClick={() => {
          handleSelectView(view.id)
          if (!isModified) setOpen(false)
        }}
        disabled={isLoading}
        className={cn(
          'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left',
          'transition-all duration-150 hover:bg-accent/50',
          'group cursor-pointer',
          isActive && 'bg-accent/30',
        )}
      >
        <div
          className={cn(
            'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md',
            bgColor,
            'transition-transform duration-150 group-hover:scale-105',
          )}
        >
          <Icon className={cn('h-3.5 w-3.5', color)} weight="duotone" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'truncate text-sm font-medium',
                isActive ? 'text-foreground' : 'text-foreground/80',
                'transition-colors group-hover:text-foreground',
              )}
            >
              {view.name}
            </span>
            {showEdited && (
              <span className="flex-shrink-0 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                Edited
              </span>
            )}
            {isOrgDefault && (
              <StarIcon
                className="h-3 w-3 flex-shrink-0 text-amber-500"
                weight="fill"
              />
            )}
          </div>
        </div>
        {isActive && (
          <CheckIcon
            className="h-4 w-4 flex-shrink-0 text-primary"
            weight="bold"
          />
        )}
      </button>
    )
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen} modal={true}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn(filterChipTriggerClasses(!!activeView), className)}
          >
            <span className="max-w-[140px] truncate">
              {activeView?.name || 'View'}
            </span>
            {isModified && (
              <span className="rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                Edited
              </span>
            )}
            <CaretDownIcon className="h-3 w-3 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className={cn(
            'w-72 p-0',
            'shadow-lg border-slate-200/80 dark:border-slate-700/50',
            'bg-white dark:bg-slate-900',
            'overflow-hidden',
          )}
        >
          <div
            className="p-2"
            style={{
              maxHeight: '70vh',
              overflowY: 'auto',
              overscrollBehavior: 'contain',
            }}
            onWheel={e => e.stopPropagation()}
          >
            {/* Shared Views Section */}
            {orgViews.length > 0 && (
              <div className="mb-2">
                <div className="px-3 pb-2 pt-1.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-500">
                    Shared Views
                  </span>
                </div>
                <div className="space-y-0.5">
                  {orgViews.map(view => (
                    <ViewItem
                      key={view.id}
                      view={view}
                      isActive={activeViewId === view.id}
                      isOrgDefault={orgDefaultViewId === view.id}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Personal Views Section */}
            {userViews.length > 0 && (
              <div className={cn(orgViews.length > 0 && 'mt-3')}>
                <div className="px-3 pb-2 pt-1.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-blue-500">
                    My Views
                  </span>
                </div>
                <div className="space-y-0.5">
                  {userViews.map(view => (
                    <ViewItem
                      key={view.id}
                      view={view}
                      isActive={activeViewId === view.id}
                      isOrgDefault={false}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {views.length === 0 && (
              <div className="px-3 py-6 text-center">
                <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-muted/50">
                  <UsersIcon className="h-5 w-5 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground">
                  No saved views yet
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Create one below to get started
                </p>
              </div>
            )}

            {/* Actions Section */}
            <div
              className={cn(
                'mt-3 border-t border-border/50 pt-3',
                views.length === 0 && 'mt-0 border-t-0 pt-2',
              )}
            >
              {/* Current View Actions - only show when there's an active view */}
              {activeView && (
                <div className="mb-2">
                  <div className="px-3 pb-1.5 pt-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Current View
                    </span>
                  </div>
                  <div className="space-y-0.5">
                    <button
                      onClick={() => {
                        handleSaveCurrent()
                        setOpen(false)
                      }}
                      disabled={isLoading || !hasConfig || !isModified}
                      className={cn(
                        'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm',
                        'transition-all duration-150',
                        'disabled:cursor-not-allowed disabled:opacity-50',
                        'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                      )}
                    >
                      <FloppyDiskIcon className="h-4 w-4" weight="regular" />
                      <span>Save changes</span>
                    </button>
                    <button
                      onClick={handleRevertChanges}
                      disabled={isLoading || !isModified}
                      className={cn(
                        'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm',
                        'transition-all duration-150',
                        'disabled:cursor-not-allowed disabled:opacity-50',
                        'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                      )}
                    >
                      <ArrowCounterClockwiseIcon
                        className="h-4 w-4"
                        weight="regular"
                      />
                      <span>Revert changes</span>
                    </button>
                    {canManage &&
                      activeView.scope === PreferenceScopeEnum.ORGANIZATION && (
                        <button
                          onClick={() => {
                            handleSetOrgDefault()
                            setOpen(false)
                          }}
                          disabled={
                            isLoading || activeView.id === orgDefaultViewId
                          }
                          className={cn(
                            'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm',
                            'transition-all duration-150',
                            'disabled:cursor-not-allowed disabled:opacity-50',
                            'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                          )}
                        >
                          <StarIcon className="h-4 w-4" weight="regular" />
                          <span>Set as org default</span>
                        </button>
                      )}
                    {canDeleteActiveView && (
                      <button
                        onClick={() => handleDeleteDialogOpenChange(true)}
                        disabled={isLoading}
                        className={cn(
                          'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm',
                          'transition-all duration-150',
                          'disabled:cursor-not-allowed disabled:opacity-50',
                          'text-destructive hover:bg-destructive/10',
                        )}
                      >
                        <TrashIcon className="h-4 w-4" weight="regular" />
                        <span>Delete view</span>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Share Section — works with or without a saved view */}
              <div
                className={cn(activeView && 'border-t border-border/30 pt-2')}
              >
                <div className="px-3 pb-1.5 pt-1">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Share
                  </span>
                </div>
                <button
                  onClick={() => {
                    void handleCopyShareLink()
                    setOpen(false)
                  }}
                  disabled={isLoading || !hasConfig}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm',
                    'transition-all duration-150',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                    'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                  )}
                >
                  <LinkIcon className="h-4 w-4" weight="regular" />
                  <span>Copy link to this view</span>
                </button>
              </div>

              {/* Save As Section */}
              <div className="border-t border-border/30 pt-2">
                <div className="px-3 pb-1.5 pt-1">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Save As
                  </span>
                </div>
                <div className="space-y-0.5">
                  <button
                    onClick={() => {
                      handleOpenCreateDialog(PreferenceScopeEnum.USER)
                      setOpen(false)
                    }}
                    disabled={isLoading || !hasConfig}
                    className={cn(
                      'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm',
                      'transition-all duration-150',
                      'disabled:cursor-not-allowed disabled:opacity-50',
                      'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                    )}
                  >
                    <UserIcon className="h-4 w-4" weight="regular" />
                    <span>Save as personal view</span>
                  </button>
                  {canManage && (
                    <button
                      onClick={() => {
                        handleOpenCreateDialog(PreferenceScopeEnum.ORGANIZATION)
                        setOpen(false)
                      }}
                      disabled={isLoading || !hasConfig}
                      className={cn(
                        'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm',
                        'transition-all duration-150',
                        'disabled:cursor-not-allowed disabled:opacity-50',
                        'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                      )}
                    >
                      <UsersIcon className="h-4 w-4" weight="regular" />
                      <span>Save as shared view</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <DialogPattern
        open={isCreateDialogOpen}
        onOpenChange={handleDialogOpenChange}
        title={dialogTitle}
        description={dialogDescription}
        icon={<ScopeIcon className={cn('h-5 w-5', scopeDetails.color)} />}
        iconSize="sm"
        isForm={true}
        onSubmit={handleCreateSubmit}
        isSubmitting={isSaving}
        maxWidth="sm:max-w-[360px]"
        footer={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleDialogOpenChange(false)}
              disabled={isSaving}
              className="h-8 rounded-full px-4 text-sm"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!isNameValid || isSaving}
              className="ml-2 h-8 rounded-full px-4 text-sm"
            >
              {pendingScope === PreferenceScopeEnum.ORGANIZATION
                ? 'Save shared view'
                : 'Save view'}
            </Button>
          </>
        }
      >
        <div className="space-y-2">
          <Label htmlFor="view-name">View name</Label>
          <Input
            id="view-name"
            value={viewName}
            onChange={event => setViewName(event.target.value)}
            placeholder="E.g. Weekly pipeline"
            autoFocus
          />
        </div>
      </DialogPattern>

      <DialogPattern
        open={isSwitchDialogOpen}
        onOpenChange={handleSwitchDialogOpenChange}
        title="Unsaved changes"
        description="You have unsaved changes to this view. Would you like to save them before switching?"
        icon={
          <WarningIcon className="h-5 w-5 text-amber-500" weight="duotone" />
        }
        iconSize="sm"
        titleSize="sm"
        maxWidth="sm:max-w-[380px]"
        hideCloseButton
        contentClassName="py-0"
        footer={
          <div className="flex w-full gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleDiscardAndSwitch}
              disabled={isSwitching}
              className="h-9 flex-1 rounded-lg text-sm"
            >
              Discard
            </Button>
            <Button
              type="button"
              onClick={handleSaveAndSwitch}
              disabled={isSwitching}
              className="h-9 flex-1 rounded-lg text-sm"
            >
              Save & switch
            </Button>
          </div>
        }
      >
        <div />
      </DialogPattern>

      <DialogPattern
        open={isDeleteDialogOpen}
        onOpenChange={handleDeleteDialogOpenChange}
        title="Delete view?"
        description={`Are you sure you want to delete "${activeView?.name}"? This action cannot be undone.`}
        icon={
          <TrashIcon className="h-5 w-5 text-destructive" weight="duotone" />
        }
        iconSize="sm"
        titleSize="sm"
        maxWidth="sm:max-w-[380px]"
        hideCloseButton
        contentClassName="py-0"
        footer={
          <div className="flex w-full gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleDeleteDialogOpenChange(false)}
              disabled={isDeleting}
              className="h-9 flex-1 rounded-lg text-sm"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteActiveView}
              disabled={isDeleting}
              className="h-9 flex-1 rounded-lg text-sm"
            >
              Delete
            </Button>
          </div>
        }
      >
        <div />
      </DialogPattern>
    </>
  )
}
